import React, { useState } from "react";
import Logo from "../assets/images/logo.png";
import { signInWithEmailAndPassword, sendPasswordResetEmail, signOut } from "firebase/auth";
import { auth } from "../lib/firebase";
import { verifyOrInitAdmin } from "../lib/firestoreContent";

const AdminLogin = ({ onLogin }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetStatus, setResetStatus] = useState({ sending: false, message: "", isError: false });

  const handleForgotPassword = async () => {
    const email = form.email.trim();
    if (!email || !email.includes("@")) {
      setResetStatus({ sending: false, message: "Please enter your email address above first.", isError: true });
      return;
    }
    setResetStatus({ sending: true, message: "", isError: false });
    try {
      await sendPasswordResetEmail(auth, email);
      setResetStatus({
        sending: false,
        message: `Password reset email sent to ${email}. Check your inbox (and spam folder).`,
        isError: false,
      });
    } catch (err) {
      console.error("Password reset error:", err);
      const code = err?.code || "";
      if (code === "auth/user-not-found") {
        setResetStatus({ sending: false, message: "No account found with this email. Ask a Super Admin to set up your account.", isError: true });
      } else if (code === "auth/too-many-requests") {
        setResetStatus({ sending: false, message: "Too many requests. Please try again later.", isError: true });
      } else {
        setResetStatus({ sending: false, message: err.message || "Failed to send reset email.", isError: true });
      }
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    try {
      const userCredential = await signInWithEmailAndPassword(auth, form.email.trim(), form.password);
      const user = userCredential.user;

      // Verify that this account is in the authorized admins list
      let verification = { isAuthorized: false, role: null };
      try {
        verification = await verifyOrInitAdmin(user);
      } catch (verifyErr) {
        console.warn("verifyOrInitAdmin warning:", verifyErr);
        if (user.email && user.email.toLowerCase().trim() === "ukanet4christ@gmail.com") {
          verification = { isAuthorized: true, role: "Super Admin" };
        } else {
          throw verifyErr;
        }
      }

      if (!verification.isAuthorized) {
        await signOut(auth);
        setError("Access Denied: This account is not authorized as an administrator. Please contact your Super Admin for access.");
        return;
      }

      if (onLogin) onLogin(user, verification.role);
    } catch (err) {
      console.error("Login error:", err);
      const code = err?.code || "";
      if (code === "auth/user-not-found" || code === "auth/wrong-password" || code === "auth/invalid-credential") {
        setError("Invalid credentials. Please verify your email and password.");
      } else if (code === "auth/too-many-requests") {
        setError("Too many failed attempts. Please try again later.");
      } else {
        setError(err.message || "An error occurred during authentication.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="grid min-h-screen w-full overflow-hidden bg-white lg:grid-cols-[0.9fr_1.1fr]">
        <section className="hidden flex-col justify-between bg-[#231651] p-10 xl:p-16 lg:flex">
          <div>
            <img src={Logo} alt="XploreStem logo" className="h-auto w-36 rounded-xl bg-white p-2" />
            <p className="mt-12 text-xs font-semibold uppercase tracking-[0.24em] text-[#d9d5ec]">Administration</p>
            <h1 className="mt-4 max-w-sm text-4xl font-bold leading-tight text-white">Manage learning that moves people forward.</h1>
            <p className="mt-5 max-w-sm text-base leading-7 text-[#d9d5ec]">Access registrations, programs, instructors, and the XploreStem content workspace.</p>
          </div>
          <p className="text-sm text-[#d9d5ec]">XploreStem internal workspace</p>
        </section>

        <section className="flex items-center justify-center p-7 sm:p-10 lg:p-14">
          <div className="w-full max-w-md">
          <div className="mb-8">
            <img src={Logo} alt="XploreStem logo" className="h-auto w-28 rounded-xl bg-white p-1 ring-1 ring-[#e2e8f0] lg:hidden" />
            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.22em] text-[#625a88]">Restricted access</p>
            <h2 className="mt-3 text-3xl font-bold text-[#231651]">
              Admin Sign In
            </h2>
            <p className="mt-2 text-sm text-[#64748b]">
              Sign in with your authorized admin credentials to access the workspace.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="admin-email" className="mb-2 block text-sm font-semibold text-[#1e293b]">Email address</label>
              <input
                id="admin-email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="w-full rounded-xl border border-[#cbd5e1] bg-white px-4 py-3.5 text-[#1e293b] outline-none transition placeholder:text-[#94a3b8] focus:border-primary focus:ring-4 focus:ring-primary/10"
                placeholder="admin@xplorestemi.org"
                required
              />
            </div>

            <div>
              <label htmlFor="admin-password" className="mb-2 block text-sm font-semibold text-[#1e293b]">Password</label>
              <div className="relative">
                <input
                  id="admin-password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-[#cbd5e1] bg-white px-4 py-3.5 pr-16 text-[#1e293b] outline-none transition placeholder:text-[#94a3b8] focus:border-primary focus:ring-4 focus:ring-primary/10"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg px-2 py-1 text-xs font-semibold text-primary hover:bg-[#f2f0f8]"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <div className="mt-1 flex justify-end">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={resetStatus.sending}
                  className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
                >
                  {resetStatus.sending ? "Sending…" : "Forgot password?"}
                </button>
              </div>
              {resetStatus.message && (
                <p
                  role="status"
                  className={`rounded-lg px-3 py-2.5 text-sm leading-relaxed ${
                    resetStatus.isError
                      ? "bg-[#fef2f2] text-[#b91c1c]"
                      : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  }`}
                >
                  {resetStatus.message}
                </p>
              )}
            </div>

            {error && <p role="alert" className="rounded-lg bg-[#fef2f2] px-3 py-2.5 text-sm text-[#b91c1c] leading-relaxed">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-primary px-4 py-3.5 font-semibold text-white shadow-[0_8px_18px_rgba(35,22,81,0.22)] transition hover:bg-[#1b1449] focus:outline-none focus:ring-4 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? "Verifying credentials…" : "Sign In to Admin Workspace"}
            </button>

            <div className="rounded-xl bg-slate-50 p-4 text-center border border-slate-100">
              <p className="text-xs text-slate-500 leading-relaxed">
                Administrator access is invite-only. To request access, please contact an existing Super Admin.
              </p>
            </div>
          </form>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminLogin;
